import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import Handlebars from "handlebars";
import { sanitizeDocumentContent } from "@/lib/sanitize";

interface DocumentPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: string | null;
}

export function DocumentPreviewDialog({ open, onClose, documentId }: DocumentPreviewDialogProps) {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [isPdf, setIsPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [signatureStatus, setSignatureStatus] = useState<{
    total: number;
    collected: number;
    pending: string[];
  } | null>(null);
  
  useEffect(() => {
    if (open && documentId) {
      loadDocumentContent();
    }
  }, [open, documentId]);
  
  const loadDocumentContent = async () => {
    if (!documentId) return;
    
    setLoading(true);
    try {
      // Load document with sections
      const { data: doc, error: docError } = await supabase
        .from('generated_documents')
        .select(`
          *,
          document_sections(*)
        `)
        .eq('id', documentId)
        .single();
      
      if (docError) throw docError;
      
      if (doc) {
        setDocumentName(doc.document_name);
        
        // Store PDF URL for download/preview - add version query param to force refresh
        if (doc.file_url) {
          const urlWithVersion = `${doc.file_url}?v=${doc.signature_version || 1}&t=${Date.now()}`;
          setPdfUrl(urlWithVersion);
        }
        
        // Check signature status if document requires signatures
        if (doc.requires_signatures) {
          const { data: requests } = await supabase
            .from('signature_requests')
            .select(`
              id,
              status,
              requested_from,
              role_signing_as,
              profiles:requested_from (
                full_name
              )
            `)
            .eq('document_id', documentId);
            
          if (requests) {
            const total = requests.length;
            const collected = requests.filter(r => r.status === 'completed').length;
            const pending = requests
              .filter(r => r.status === 'pending')
              .map(r => {
                const profile = r.profiles as any;
                return profile?.full_name || r.role_signing_as || 'Unknown Signer';
              });
            setSignatureStatus({ total, collected, pending });
          }
        }
        
        // Always build HTML preview from sections or custom content
        setIsPdf(false);
        // Check for custom_content FIRST (regardless of is_custom flag) - matches DocumentPreviewModal pattern
        if (doc.custom_content) {
            setContent(doc.custom_content);
          } else if (doc.document_sections && Array.isArray(doc.document_sections) && doc.document_sections.length > 0) {
            // Check if we have merge_data to apply
            const hasMergeData = doc.merge_data && Object.keys(doc.merge_data).length > 0;
            
            if (hasMergeData) {
              // Fetch the original template to get raw {{placeholders}}
              const templateId = doc.template_library_id || doc.template_id;
              const templateTable = doc.template_library_id 
                ? 'document_template_library' 
                : 'document_templates';
              
              const { data: template } = await supabase
                .from(templateTable)
                .select('template_content')
                .eq('id', templateId)
                .order('version', { ascending: false })
                .limit(1)
                .maybeSingle();
              
              if (template && template.template_content) {
                // Compile template with current merge_data
                try {
                  // IMPORTANT: Convert string clauses to arrays for {{#each}} helper
                  const preparedMergeData = { ...(doc.merge_data as Record<string, any> || {}) };
                  
                  // Convert whereas_clauses to array if it's a string
                  if (typeof preparedMergeData.whereas_clauses === 'string') {
                    preparedMergeData.whereas_clauses = preparedMergeData.whereas_clauses
                      .split('\n')
                      .map((line: string) => line.trim())
                      .filter((line: string) => line.length > 0);
                  }
                  
                  // Convert resolved_clauses to array if it's a string
                  if (typeof preparedMergeData.resolved_clauses === 'string') {
                    preparedMergeData.resolved_clauses = preparedMergeData.resolved_clauses
                      .split('\n')
                      .map((line: string) => line.trim())
                      .filter((line: string) => line.length > 0);
                  }
                  
                  const compiledTemplate = Handlebars.compile(template.template_content);
                  const renderedContent = compiledTemplate(preparedMergeData);
                  setContent(renderedContent);
                } catch (error) {
                  console.error('Error rendering template:', error);
                  // Fallback to section content
                  const html = doc.document_sections
                    .sort((a: any, b: any) => a.section_number - b.section_number)
                    .map((s: any) => `
                      <div class="section">
                        <h2 style="font-size: 1.5em; font-weight: bold; margin: 1em 0 0.5em 0;">${s.section_title || ''}</h2>
                        <div>${s.section_content || ''}</div>
                      </div>
                    `)
                    .join('<hr style="margin: 2em 0; border: 1px solid #e5e7eb;"/>');
                  setContent(html);
                }
              } else {
                // No template found, use section content as-is
                const html = doc.document_sections
                  .sort((a: any, b: any) => a.section_number - b.section_number)
                  .map((s: any) => `
                    <div class="section">
                      <h2 style="font-size: 1.5em; font-weight: bold; margin: 1em 0 0.5em 0;">${s.section_title || ''}</h2>
                      <div>${s.section_content || ''}</div>
                    </div>
                  `)
                  .join('<hr style="margin: 2em 0; border: 1px solid #e5e7eb;"/>');
                setContent(html);
              }
            } else {
              // No merge data yet, use section content as-is
              const html = doc.document_sections
                .sort((a: any, b: any) => a.section_number - b.section_number)
                .map((s: any) => `
                  <div class="section">
                    <h2 style="font-size: 1.5em; font-weight: bold; margin: 1em 0 0.5em 0;">${s.section_title || ''}</h2>
                    <div>${s.section_content || ''}</div>
                  </div>
                `)
                .join('<hr style="margin: 2em 0; border: 1px solid #e5e7eb;"/>');
              setContent(html);
            }
          } else {
            setContent("<div style='padding: 2em; text-align: center; color: #6b7280;'><p>No content available. This document may not have been properly generated.</p><p style='margin-top: 1em; font-size: 0.875em;'>Try regenerating the document from the workflow.</p></div>");
          }
        }
    } catch (error) {
      console.error('Error loading document:', error);
      setContent("<p>Error loading document preview.</p>");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{documentName || "Document Preview"}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Signature Status Banner */}
            {signatureStatus && (
              <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-blue-900">
                    Signatures: {signatureStatus.collected}/{signatureStatus.total}
                  </span>
                  {signatureStatus.collected === signatureStatus.total ? (
                    <span className="text-sm text-green-600 font-medium">✓ All signatures collected</span>
                  ) : (
                    <span className="text-sm text-amber-600 font-medium">Pending signatures</span>
                  )}
                </div>
                {signatureStatus.pending.length > 0 && (
                  <div className="text-sm text-blue-700 mt-2">
                    Awaiting: {signatureStatus.pending.join(', ')}
                  </div>
                )}
              </div>
            )}
            
            {/* Always render HTML content - Chrome blocks iframe PDFs */}
            <div 
              className="ecclesiastical-document max-w-none p-6 border rounded-lg"
              dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(content) }}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
